import { create } from 'zustand'

import { SERVICE_WORKER_URL } from '../notifications/service-worker-url'

/*
 * App instalável (sprint-14-spec §4): o evento `beforeinstallprompt` (Edge e Chrome) fica guardado
 * aqui para o item "Instalar o app" do menu da conta, e o service worker do push é registrado
 * depois do login (o navegador exige um para instalar). Sem cache: o `sw.js` não intercepta
 * requisições.
 */

/** O evento do Chromium (não está nos tipos do DOM). */
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform?: string }>
}

interface InstallState {
  /** Pedido de instalação guardado (só Edge e Chrome, e só enquanto o app não está instalado). */
  promptEvent: BeforeInstallPromptEvent | null
  /** Rodando como app instalado ou instalado nesta visita. */
  installed: boolean
}

export const useInstallStore = create<InstallState>()(() => ({
  promptEvent: null,
  installed: false,
}))

/** Aberto como app instalado (janela própria)? */
export function runningStandalone(): boolean {
  if (typeof window === 'undefined') return false
  const matches = (query: string) =>
    typeof window.matchMedia === 'function' && window.matchMedia(query).matches
  const standalone =
    matches('(display-mode: standalone)') || matches('(display-mode: window-controls-overlay)')
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true
  return standalone || iosStandalone
}

let listening = false

/**
 * Escuta `beforeinstallprompt` e `appinstalled` desde o carregamento (o evento pode chegar ainda na
 * tela de login). Idempotente.
 */
export function listenForInstallPrompt(): void {
  if (listening || typeof window === 'undefined') return
  listening = true
  useInstallStore.setState({ installed: runningStandalone() })
  window.addEventListener('beforeinstallprompt', (event) => {
    // Sem a mini barra automática do navegador: a instalação parte do menu da conta.
    event.preventDefault()
    useInstallStore.setState({ promptEvent: event as BeforeInstallPromptEvent })
  })
  window.addEventListener('appinstalled', () => {
    useInstallStore.setState({ promptEvent: null, installed: true })
  })
}

/** Só para os testes: volta ao estado inicial. */
export function resetInstallState(): void {
  listening = false
  useInstallStore.setState({ promptEvent: null, installed: false })
}

export type InstallOutcome = 'accepted' | 'dismissed' | 'unavailable'

/** Mostra o pedido do navegador. Sem pedido guardado: `unavailable` (a tela mostra os passos). */
export async function promptInstall(): Promise<InstallOutcome> {
  const event = useInstallStore.getState().promptEvent
  if (!event) return 'unavailable'
  // O evento só pode ser usado uma vez.
  useInstallStore.setState({ promptEvent: null })
  await event.prompt()
  const choice = await event.userChoice
  if (choice.outcome === 'accepted') useInstallStore.setState({ installed: true })
  return choice.outcome
}

/**
 * Registra o `sw.js` (o mesmo do push) depois do login. Falha silenciosa: sem service worker o app
 * só não fica instalável, e o push tenta de novo ao ser ativado.
 */
export async function registerAppServiceWorker(): Promise<boolean> {
  try {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return false
    await navigator.serviceWorker.register(SERVICE_WORKER_URL, { scope: '/' })
    return true
  } catch {
    return false
  }
}
